'use client';

import {
  ChevronRight,
  Shield,
  LayoutDashboard,
  Users,
  Database,
  UserPlus,
  ShieldCheck,
  Trophy,
  MessageSquare,
  Eye,
  FileText,
  Search,
  Zap,
  Globe,
  Webhook,
  AlertTriangle,
  ClipboardList,
  Download,
} from 'lucide-react';

export interface Screen {
  heading: string;
  subtitle?: string;
  icon: typeof Shield;
  iconBg: string;
  iconColor: string;
  body: React.ReactNode;
}

export const SCREENS: Screen[] = [
  /* -- 1. Insurance Is Different -- */
  {
    heading: 'Insurance Is Different',
    subtitle: 'The communication infrastructure for insurance agencies.',
    icon: Shield,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    body: (
      <>
        <p className="text-[15px]">
          Generic messaging tools weren&rsquo;t built for insurance. Your agency manages
          sensitive client data, E&amp;O liability, carrier compliance requirements, and
          multi-agent workflows &mdash; all at once.
        </p>
        <p className="text-[15px]">
          Voley was <strong>designed by agency owners</strong> to accomplish three things:
        </p>
        <div className="grid gap-2.5 mt-1">
          {[
            { label: 'Stop missing leads', desc: 'Every inquiry captured, every response tracked' },
            { label: 'Reduce risk & exposure', desc: 'Real-time guardrails and complete documentation' },
            { label: 'Visibility into communication', desc: 'See every client conversation across your agency' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/50 p-3">
              <div className="flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 text-white shrink-0 mt-0.5">
                <ChevronRight className="h-3 w-3" />
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-900">{item.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },

  /* -- 2. One Inbox. Zero Complexity. -- */
  {
    heading: 'One Inbox. Zero Complexity.',
    subtitle: 'Purpose-built simplicity your team will actually use.',
    icon: LayoutDashboard,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    body: (
      <>
        <p className="text-[15px]">
          Voley isn&rsquo;t another app to juggle &mdash; it&rsquo;s designed to <strong>connect
          everything you already use</strong>. One inbox, one system, one source of truth
          for all communication across your different systems.
        </p>
        <div className="grid grid-cols-2 gap-2.5 mt-1">
          {[
            { icon: LayoutDashboard, label: 'Clean Dashboard', desc: 'See everything at a glance' },
            { icon: Zap, label: 'Zero Training', desc: 'Intuitive from day one' },
            { icon: MessageSquare, label: 'SMS & MMS', desc: 'Text, images, and files' },
            { icon: Users, label: 'Built for Teams', desc: 'Agents and admins together' },
          ].map((item) => (
            <div key={item.label} className="flex flex-col items-center text-center rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
              <item.icon size={20} className="text-emerald-600 mb-1.5" />
              <span className="font-semibold text-sm text-gray-900">{item.label}</span>
              <span className="text-xs text-gray-500">{item.desc}</span>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-3 italic">
          &ldquo;We went from three tools and a shared spreadsheet to just Voley.
          Onboarding took 15 minutes.&rdquo;
        </p>
      </>
    ),
  },

  /* -- 3. Agency-Wide Visibility -- */
  {
    heading: 'Agency-Wide Visibility',
    subtitle: 'Leadership has visibility. Agents have clarity. Conversations don\u2019t disappear.',
    icon: Eye,
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-600',
    body: (
      <>
        <p className="text-[15px]">
          Every client message lives in one place. Assign conversations, filter by status,
          and see exactly who said what &mdash; across your entire agency.
        </p>
        <div className="flex flex-col gap-2.5 mt-1">
          {[
            { icon: MessageSquare, label: 'Unified Agency Number', desc: 'One professional number for SMS and MMS. Clients text your agency, not personal phones.' },
            { icon: Users, label: 'Assignments & Handoffs', desc: 'Assign conversations to the right agent. Transfer, collaborate, and stay organized without dropping context.' },
            { icon: Search, label: 'Filtering & Organization', desc: 'Filter by tags, assignees, read status, and views. Find any conversation in seconds.' },
            { icon: Eye, label: 'Full Audit Trail', desc: 'Every message timestamped with sender identity. Nothing gets lost, deleted, or hidden.' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-violet-100 shrink-0 mt-0.5">
                <item.icon size={16} className="text-violet-600" />
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-900">{item.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },

  /* -- 4. Your AMS, Supercharged -- */
  {
    heading: 'Your AMS, Supercharged',
    subtitle: 'Your AMS stays the system of record. Voley just makes it effortless.',
    icon: Database,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    body: (
      <>
        <p className="text-[15px]">
          Deep, native AMS integration means every conversation is documented &mdash;
          with timestamps, sender names, and full context. No copy-pasting. No missing messages.
        </p>
        <div className="flex flex-col gap-2.5 mt-1">
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={14} className="text-amber-600" />
              <span className="font-semibold text-sm text-gray-900">Smart Logging</span>
            </div>
            <p className="text-xs text-gray-500">
              Log as searchable text or clean reference links. Attach follow-up tasks with
              assignees and due dates. MMS and files upload automatically.
            </p>
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={14} className="text-amber-600" />
              <span className="font-semibold text-sm text-gray-900">Automatic Logging</span>
            </div>
            <p className="text-xs text-gray-500">
              Log-on-close prompts and end-of-day auto-logging ensure nothing slips through.
              Smart format adapts to conversation length automatically.
            </p>
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList size={14} className="text-amber-600" />
              <span className="font-semibold text-sm text-gray-900">Logging History</span>
            </div>
            <p className="text-xs text-gray-500">
              See exactly what was logged, when, and by whom. Complete accountability
              and easy retrieval when you need records.
            </p>
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <LayoutDashboard size={14} className="text-amber-600" />
              <span className="font-semibold text-sm text-gray-900">Native AMS Workflow</span>
            </div>
            <p className="text-xs text-gray-500">
              Use Voley messaging directly inside your AMS &mdash; no switching between tools.
              Send, receive, and manage conversations without ever leaving the system you already work in.
            </p>
          </div>
        </div>
      </>
    ),
  },

  /* -- 5. Capture Every Opportunity -- */
  {
    heading: 'Capture Every Opportunity',
    subtitle: 'This is where revenue leaks usually happen.',
    icon: UserPlus,
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    body: (
      <>
        <p className="text-[15px]">
          No more <em>&ldquo;who got that message?&rdquo;</em> or{' '}
          <em>&ldquo;did anybody respond?&rdquo;</em> Every inquiry is captured, routed,
          and visible to your entire team.
        </p>
        <div className="flex flex-col gap-2.5 mt-1">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-green-100 shrink-0 mt-0.5">
              <Globe size={16} className="text-green-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">Website Widget</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Embed Voley on your website. Visitors text your agency number directly &mdash;
                the conversation starts before they even call.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-green-100 shrink-0 mt-0.5">
              <Database size={16} className="text-green-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">CRM Integration</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Connect your CRM to streamline lead intake and contact management.
                New conversations automatically sync with your existing workflows.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-green-100 shrink-0 mt-0.5">
              <Webhook size={16} className="text-green-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">Webhook Support</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Trigger automations in your existing tools when messages arrive,
                conversations close, or leads come in.
              </p>
            </div>
          </div>
        </div>
      </>
    ),
  },

  /* -- 6. Compliance Without Compromise -- */
  {
    heading: 'Compliance Without Compromise',
    subtitle: 'Prove what was actually said. Prevent errors before they happen.',
    icon: ShieldCheck,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    body: (
      <>
        <p className="text-[15px]">
          Insurance communication carries real legal weight. Voley gives you the tools
          to <strong>protect your agency</strong> without slowing your team down.
        </p>
        <div className="flex flex-col gap-2.5 mt-1">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-red-100 shrink-0 mt-0.5">
              <AlertTriangle size={16} className="text-red-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">E&amp;O Guardrails</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Real-time scanning flags risky language before messages are sent. Your team
                learns in the moment without being blocked or micromanaged.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-red-100 shrink-0 mt-0.5">
              <ShieldCheck size={16} className="text-red-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">Custom Guardrails &amp; Agent Flagging</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Define your own risk phrases. Get notified when agents trigger warnings
                so you can coach proactively instead of reactively.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-red-100 shrink-0 mt-0.5">
              <Download size={16} className="text-red-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">Audit History &amp; Downloads</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Complete, searchable records of every conversation. Download detailed
                audit reports when you need documentation for carriers, regulators, or legal.
              </p>
            </div>
          </div>
        </div>
      </>
    ),
  },

  /* -- 7. Why Agencies Choose Voley -- */
  {
    heading: 'Why Agencies Choose Voley',
    icon: Trophy,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    body: (
      <>
        <p className="text-[15px] mb-1">
          At the end of the day, agencies use Voley for three reasons:
        </p>
        <div className="flex flex-col gap-3 mt-1">
          {[
            {
              num: '1',
              title: 'They stop missing leads',
              desc: 'Every inquiry captured and routed. No more lost texts or forgotten follow-ups.',
              color: 'bg-accent-blue',
            },
            {
              num: '2',
              title: 'They reduce risk and exposure',
              desc: 'Real-time guardrails, complete documentation, and auditable records.',
              color: 'bg-accent-blue',
            },
            {
              num: '3',
              title: 'They finally have visibility',
              desc: 'See every client conversation across your entire agency in one place.',
              color: 'bg-accent-blue',
            },
          ].map((item) => (
            <div key={item.num} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className={`flex items-center justify-center h-7 w-7 rounded-full ${item.color} text-white text-xs font-bold shrink-0 mt-0.5`}>
                {item.num}
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-900">{item.title}</span>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-lg bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-100 p-4 text-center">
          <p className="text-sm font-medium text-gray-800">
            &ldquo;If Voley helps you capture <strong>one additional opportunity</strong> or
            prevents <strong>one documentation issue</strong>, it pays for itself.&rdquo;
          </p>
        </div>
      </>
    ),
  },
];
